import { createContext, useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import PropTypes from 'prop-types'
import { useAPI } from 'src/utils/api'
import { journeyUrl, analyticsQueryUrl } from 'src/configs/api'
import {
  mixJourneyDataWithReport,
  findMatchingReportNode
} from 'src/views/apps/engagement/marketing-automation/journey-builder/utils/reportUtils'

const CHANNEL_CONFIG = {
  email: { icon: 'mdi:email-outline', color: '#1D9E75' },
  sms: { icon: 'mdi:message-text-outline', color: '#D85A30' },
  whatsapp: { icon: 'mdi:whatsapp', color: '#2E7D32' },
  rcs: { icon: 'mdi:sms', color: '#FF9800' }
}

const defaultProvider = {
  activeTab: 'Engagements',
  setActiveTab: () => null,
  selectedChannels: [],
  setSelectedChannels: () => null,
  selectedJourney: null,
  setSelectedJourney: () => null,
  isJourneyLoading: false,
  setIsJourneyLoading: () => null,
  reportDate: '',
  setReportDate: () => null,
  handleChannelChange: () => null,
  journeyReport: null,
  totalReport: null,
  particularReport: null,
  executionDates: [],
  setJourneyReport: () => null,
  isReportLoading: false,
  setIsReportLoading: () => null,
  getJourneyReport: () => Promise.resolve(null),
  fetchTopJourneys: () => Promise.resolve({ top_by_open_rate: [], top_by_click_rate: [] }),
  mixJourneyDataWithReport: (nodes, _report) => nodes,
  findMatchingReportNode: (_node, _report) => null,
  selectedChannelNodeId: 'all',
  setSelectedChannelNodeId: () => null,
  channelNodes: []
}

const getChannelConfig = node => {
  if (!node || typeof node !== 'object') {
    return null
  }
  const typeStr = (node.type || '').toLowerCase()
  const subtypeStr = (node.subtype || node.item_type || node.channel || '').toLowerCase()

  if (CHANNEL_CONFIG[typeStr]) {
    return { type: typeStr, cfg: CHANNEL_CONFIG[typeStr] }
  }
  if (CHANNEL_CONFIG[subtypeStr]) {
    return { type: subtypeStr, cfg: CHANNEL_CONFIG[subtypeStr] }
  }
  const nameStr = (node.label || node.name || '').toLowerCase()
  for (const key of Object.keys(CHANNEL_CONFIG)) {
    if (typeStr.includes(key) || subtypeStr.includes(key) || nameStr.includes(key)) {
      return { type: key, cfg: CHANNEL_CONFIG[key] }
    }
  }
  return null
}

const JourneyReportContext = createContext(defaultProvider)

/**
 * Ensures the date is formatted strictly as a bare YYYY-MM-DD string for API requests.
 * Example: '2026-08-18T10:30:00.000Z' -> '2026-08-18'
 * Returns empty string if invalid or null/undefined.
 */
const formatBareDate = val => {
  if (!val || val === 'ALL' || val === 'all') {
    return ''
  }
  if (typeof val === 'string') {
    const trimmed = val.trim()
    const ddmmyyyyMatch = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/)
    if (ddmmyyyyMatch) {
      return `${ddmmyyyyMatch[3]}-${ddmmyyyyMatch[2]}-${ddmmyyyyMatch[1]}`
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed
    }
    const parsed = new Date(trimmed)
    if (!isNaN(parsed.getTime())) {
      return parsed.toISOString().split('T')[0]
    }
  }
  if (typeof val === 'object' && val !== null) {
    const raw = val.date || val.timestamp || val.created_at || val.id || ''
    if (raw) {
      return formatBareDate(raw)
    }
    if (val instanceof Date && !isNaN(val.getTime())) {
      return val.toISOString().split('T')[0]
    }
  }
  return ''
}

/**
 * Formats a date string (YYYY-MM-DD or Date object) into user-facing DD-MM-YYYY format.
 * Example: '2026-08-21' -> '21-08-2026'
 */
const formatDisplayDate = val => {
  if (!val || val === 'ALL' || val === 'all') {
    return ''
  }
  const str = String(val).trim()
  const yyyymmddMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (yyyymmddMatch) {
    return `${yyyymmddMatch[3]}-${yyyymmddMatch[2]}-${yyyymmddMatch[1]}`
  }
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    return str
  }
  const parsed = new Date(str)
  if (!isNaN(parsed.getTime())) {
    const day = String(parsed.getDate()).padStart(2, '0')
    const month = String(parsed.getMonth() + 1).padStart(2, '0')
    const year = parsed.getFullYear()
    return `${day}-${month}-${year}`
  }
  return str
}

const extractExecutionDates = (reportData, journey) => {
  const datesSet = new Set()
  const result = []

  const addDate = d => {
    const bare = formatBareDate(d)
    if (bare && !datesSet.has(bare)) {
      datesSet.add(bare)
      result.push({ date: bare, label: formatDisplayDate(bare) })
    }
  }

  if (reportData) {
    const list =
      reportData.execution_dates ||
      reportData.executions ||
      reportData.dates ||
      reportData.history ||
      reportData.runs ||
      []
    if (Array.isArray(list)) {
      list.forEach(addDate)
    }

    const nodes = reportData.nodes || reportData.data?.nodes || []
    if (Array.isArray(nodes)) {
      nodes.forEach(n => {
        if (n?.date) {
          addDate(n.date)
        }
      })
    }
  }

  if (journey) {
    const jList =
      journey.execution_dates || journey.executions || journey.runs || journey.metadata?.execution_dates || []
    if (Array.isArray(jList)) {
      jList.forEach(addDate)
    }
    if (journey.started_at) {
      addDate(journey.started_at)
    }
    if (journey.updated_at) {
      addDate(journey.updated_at)
    }
    if (journey.created_at) {
      addDate(journey.created_at)
    }
  }

  return result
}

const JourneyReportProvider = ({ children }) => {
  const router = useRouter()
  const api = useAPI()
  const apiRef = useRef(api)

  useEffect(() => {
    apiRef.current = api
  }, [api])

  const [activeTab, setActiveTab] = useState(defaultProvider.activeTab)
  const [selectedChannels, setSelectedChannels] = useState(defaultProvider.selectedChannels)
  const [selectedJourney, setSelectedJourneyState] = useState(defaultProvider.selectedJourney)
  const [isJourneyLoading, setIsJourneyLoading] = useState(defaultProvider.isJourneyLoading)
  const [reportDate, setReportDateState] = useState(defaultProvider.reportDate)
  const [journeyReport, setJourneyReport] = useState(defaultProvider.journeyReport)
  const [totalReport, setTotalReport] = useState(null)
  const [particularReport, setParticularReport] = useState(null)
  const [executionDates, setExecutionDates] = useState([])
  const [isReportLoading, setIsReportLoading] = useState(defaultProvider.isReportLoading)
  const [selectedChannelNodeId, setSelectedChannelNodeId] = useState('all')

  const channelNodes = useMemo(() => {
    if (!selectedJourney) {
      return [{ id: 'all', label: 'All Nodes', icon: 'mdi:sitemap-outline', color: '#8892b0' }]
    }

    const rawNodes =
      selectedJourney?.definition?.nodes ||
      selectedJourney?.workflow?.definition?.nodes ||
      selectedJourney?.nodes ||
      totalReport?.nodes ||
      totalReport?.node_reports ||
      totalReport?.reports ||
      totalReport?.data?.nodes ||
      journeyReport?.nodes ||
      journeyReport?.node_reports ||
      journeyReport?.reports ||
      journeyReport?.data?.nodes ||
      []

    const nodeList = Array.isArray(rawNodes)
      ? rawNodes
      : typeof rawNodes === 'object' && rawNodes !== null
        ? Object.values(rawNodes)
        : []

    const channelItems = []
    const seenIds = new Set()

    nodeList.forEach(node => {
      if (!node || typeof node !== 'object') {
        return
      }
      const matched = getChannelConfig(node)
      if (matched) {
        const id = node.node_uid || node.id || node.data?.node_uid || node.label
        if (id && !seenIds.has(id)) {
          seenIds.add(id)
          channelItems.push({
            id,
            label: node.label || node.name || `${matched.type.toUpperCase()} Node`,
            type: matched.type,
            icon: matched.cfg.icon,
            color: matched.cfg.color
          })
        }
      }
    })

    return [{ id: 'all', label: 'All Nodes', icon: 'mdi:sitemap-outline', color: '#8892b0' }, ...channelItems]
  }, [selectedJourney, totalReport, journeyReport])

  const fetchedTotalKeyRef = useRef('')
  const fetchedParticularKeyRef = useRef('')

  const setReportDate = useCallback(val => {
    const bare = formatBareDate(val)
    setReportDateState(bare)
  }, [])

  const setSelectedJourney = useCallback(journey => {
    setSelectedJourneyState(prev => {
      if (journey && (!prev || prev.id !== journey.id)) {
        setIsJourneyLoading(true)
        setReportDateState('')
        setSelectedChannelNodeId('all')
        setParticularReport(null)
      }
      return journey
    })
  }, [])

  // Handle initial journey selection from URL params
  useEffect(() => {
    const journeyId = router.query?.journeyId
    if (journeyId && !selectedJourney) {
      // First, check if the journey details are already available in the JourneyContext (journey list)
      // Since we are in a provider, we cannot call useJourneyAuth() directly.
      // However, if this provider is wrapped by JourneyContextProvider, we might need a different approach.
      // But usually, the JourneyList is a sibling or child.
      // If we want to check a global store or a shared state, we should.

      // Given the current architecture, the most reliable way to get the full object
      // when only the ID is available in the URL is to fetch it.

      const fetchJourneyDetails = async () => {
        try {
          setIsJourneyLoading(true)
          const response = await apiRef.current.get(`${journeyUrl}/${journeyId}`)
          const journeyData = response?.data?.data || response?.data
          if (journeyData) {
            setSelectedJourneyState(journeyData)
          }
        } catch (error) {
          console.error('Error fetching journey details for URL param:', error)
        } finally {
          setIsJourneyLoading(false)
        }
      }
      fetchJourneyDetails()
    }
  }, [router.query?.journeyId, selectedJourney, setSelectedJourneyState, apiRef])

  const handleChannelChange = useCallback(ch => {
    setSelectedChannels(ch)
  }, [])

  /**
   * Fetches journey channel report from API.
   * GET /v1/api/ma/journeys/:id/channel-report?date=YYYY-MM-DD
   * Without date: returns cumulative total report.
   * With date: returns particular execution report for single calendar day.
   */
  const getJourneyReport = useCallback(
    async (id, date) => {
      const journeyId = id || selectedJourney?.id
      if (!journeyId) {
        return null
      }

      setIsReportLoading(true)
      try {
        const params = {}
        const formattedDate = formatBareDate(date)
        if (formattedDate) {
          params.date = formattedDate
        }

        const endpoint = `${journeyUrl}/${journeyId}/channel-report`
        const response = await apiRef.current.get(endpoint, { params })
        const reportData = response?.data?.data || response?.data || null

        if (reportData) {
          if (!formattedDate) {
            setTotalReport(reportData)
            setJourneyReport(reportData)
            const extracted = extractExecutionDates(reportData, selectedJourney)
            if (extracted.length > 0) {
              setExecutionDates(extracted)
            }
          } else {
            setParticularReport(reportData)
          }
        }

        return reportData
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching journey channel report:', error)
        return null
      } finally {
        setIsReportLoading(false)
      }
    },
    [selectedJourney]
  )

  // Fetch cumulative Total report when journey changes
  useEffect(() => {
    if (!selectedJourney?.id) {
      setJourneyReport(null)
      setTotalReport(null)
      setParticularReport(null)
      setExecutionDates([])
      fetchedTotalKeyRef.current = ''
      fetchedParticularKeyRef.current = ''
      setIsJourneyLoading(false)
      return
    }

    if (fetchedTotalKeyRef.current === selectedJourney.id) {
      return
    }
    fetchedTotalKeyRef.current = selectedJourney.id

    getJourneyReport(selectedJourney.id)
      .catch(() => {})
      .finally(() => {
        setIsJourneyLoading(false)
      })
  }, [selectedJourney?.id, getJourneyReport])

  // Fetch Particular Date report when reportDate changes
  useEffect(() => {
    if (!selectedJourney?.id || !reportDate) {
      setParticularReport(null)
      fetchedParticularKeyRef.current = ''
      return
    }

    const key = `${selectedJourney.id}:${reportDate}`
    if (fetchedParticularKeyRef.current === key) {
      return
    }
    fetchedParticularKeyRef.current = key

    getJourneyReport(selectedJourney.id, reportDate).catch(() => {})
  }, [selectedJourney?.id, reportDate, getJourneyReport])

  /**
   * Fetches Top 5 Journeys from API based on source: "top_journeys" and timeRange.
   * POST /v1/api/c/analytics/query
   */
  const fetchTopJourneys = useCallback(async timeRange => {
    try {
      const response = await apiRef.current.post(analyticsQueryUrl, {
        source: 'top_journeys',
        timeRange: {
          from: timeRange?.from || '',
          to: timeRange?.to || ''
        }
      })
      const data = response?.data?.data || response?.data || {}
      return {
        top_by_open_rate: data.top_by_open_rate || [],
        top_by_click_rate: data.top_by_click_rate || []
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching top journeys:', error)
      return { top_by_open_rate: [], top_by_click_rate: [] }
    }
  }, [])

  const values = useMemo(
    () => ({
      activeTab,
      setActiveTab,
      selectedChannels,
      setSelectedChannels,
      selectedJourney,
      setSelectedJourney,
      isJourneyLoading,
      setIsJourneyLoading,
      reportDate,
      setReportDate,
      handleChannelChange,
      journeyReport,
      setJourneyReport,
      totalReport,
      setTotalReport,
      particularReport,
      setParticularReport,
      executionDates,
      setExecutionDates,
      isReportLoading,
      setIsReportLoading,
      getJourneyReport,
      fetchTopJourneys,
      formatDisplayDate,
      formatBareDate,
      mixJourneyDataWithReport,
      findMatchingReportNode,
      selectedChannelNodeId,
      setSelectedChannelNodeId,
      channelNodes
    }),
    [
      activeTab,
      selectedChannels,
      selectedJourney,
      setSelectedJourney,
      isJourneyLoading,
      reportDate,
      setReportDate,
      handleChannelChange,
      journeyReport,
      totalReport,
      particularReport,
      executionDates,
      isReportLoading,
      getJourneyReport,
      fetchTopJourneys,
      selectedChannelNodeId,
      setSelectedChannelNodeId,
      channelNodes
    ]
  )

  return <JourneyReportContext.Provider value={values}>{children}</JourneyReportContext.Provider>
}

JourneyReportProvider.propTypes = {
  children: PropTypes.node.isRequired
}

export { JourneyReportContext, JourneyReportProvider }
